<template>
  <div>
    <div v-if="adding">
      <div class="flex owl">
        <div>
          <vue-cropper
            :src="page.pictureUrl"
            @crop="crop"
            :guides="false"
            :autoCropArea="0.1"
            :viewMode="1"
            :autoCrop="false"
            :rotatable="false"
            :scalable="false"
            :style="imageStyle"
          />
        </div>
        <div class="v-owl">
          <div><input v-model="linkUrl" style="width:300px;" placeholder="https://"></div>
          <div class="owl">
            <button class="bg-transparent border border-solid border-gray-300" @click="cancelAdd">Cancel</button>
            <button @click="setLink" :disabled="cropData === null || !linkUrl">Set link</button>
          </div>
          <QrCodeStatus :guess-status="guessStatus" :guess-url="guessUrl" @useGuess="useGuess" />
        </div>
      </div>
    </div>
    <div v-else-if="!!selectedLink">
      <div class="flex owl">
        <div>
          <vue-cropper
            :src="page.pictureUrl"
            @crop="crop"
            :guides="false"
            :viewMode="1"
            :rotatable="false"
            :scalable="false"
            :style="imageStyle"
            :data="getCropDataFromLink(selectedLink)"
          />
        </div>
        <div>
          <div>
            <div>Selected link</div>
            <div>URL: <a :href="selectedLink.url">{{selectedLink.url}}</a></div>
            <div><input v-model="linkUrl" style="width:300px;" placeholder="https://"></div>
            <div class="mt-2"><button @click="editLink" :disabled="!linkUrl"><i class="fas fa-save"></i> Save</button></div>
            <div class="owl">
              <button class="bg-transparent border border-solid border-gray-300" @click="cancelSelect">Cancel</button>
              <button class="bg-red-300 hover:bg-red-500" @click="delLink(selectedLink)"><i class="fas fa-trash-alt"></i> Delete</button>
            </div>
            <QrCodeStatus :guess-status="guessStatus" :guess-url="guessUrl" @useGuess="useGuess" />
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="flex owl">
        <div>
          <div class="relative inline-block top-element">
            <img ref="image" :src="page.pictureUrl" :style="imageStyle">
            <div class="absolute inset-0">
              <svg width="100%" height="100%" version="1.1" xmlns="http://www.w3.org/2000/svg" @click="cancelSelect">
                <rect
                  v-for="link of localLinks"
                  :key="link.id" v-bind="linkProps(link)"
                  class="rect"
                  fill="transparent"
                  :stroke-width="strokeWidth"
                  :stroke="getStroke(link)"
                  @click.stop="selectLink(link)"
                  cursor="pointer"
                />
              </svg>
            </div>
          </div>
        </div>
        <div>
          <button @click="adding = true">Add link</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.min.css'
import { SnotifyPosition } from 'vue-snotify'
import QrScanner from 'qr-scanner'
import QrScannerWorkerPath from '!!file-loader!../../node_modules/qr-scanner/qr-scanner-worker.min.js'
QrScanner.WORKER_PATH = QrScannerWorkerPath
import debounce from 'lodash/debounce'
import QrCodeStatus from "./QrCodeStatus";

export default {
  props: {
    page: Object,
    createLink: Function,
    updateLink: Function,
    deleteLink: Function,
    links: Array,
  },
  data() {
    return {
      adding: false,
      cropData: null,
      strokeWidth: 5,
      // Keep track of any links we add or remove after this component has received props
      localLinks: [...this.links],
      selectedLink: null,
      linkUrl: null,
      maxImageHeight: null,
      // Guess basically means "looking for QR code"
      guessUrl: null,
      guessStatus: null,
    }
  },
  mounted() {
    this.$refs.image.addEventListener('load', () => {
      const topElement = this.$el.querySelector('.top-element')
      // Adjustments means like padding and leeway.
      const adjustments = 40
      this.maxImageHeight = document.documentElement.clientHeight - topElement.getBoundingClientRect().top - adjustments

      this.guess = debounce(this.guess, 1000)
    })
  },
  computed: {
    imageStyle() {
      const style = {}
      if (this.maxImageHeight) {
        style.maxHeight = this.maxImageHeight + 'px'
      }
      return style
    },
  },
  methods: {
    useGuess() {
      this.linkUrl = this.guessUrl
      this.clearGuess()
    },
    clearGuess() {
      this.guessUrl = null
      this.guessStatus = null
    },
    guess() {
      this.guessStatus = 'guessing'
      this.guessUrl = null
      const img = new Image()
      // I got this line from here: https://stackoverflow.com/a/33135803/135101
      // I can't tell how necessary it is. The behavior seems erratic. I know caching is affecting me.
      // Hilariously, if I copy and paste the guys answer (the whole thing, which fetches an image from lorempixel.com),
      // it doesn't work. My code works for me sometimes, sometimes not.
      img.setAttribute('crossOrigin', '')
      // Force no cache.
      // See: https://stackoverflow.com/a/33536480/135101
      img.src = this.page.pictureUrl + '?' + new Date().getTime()
      img.onload = () => {
        QrScanner.scanImage(img, {
          x: this.cropData.x,
          y: this.cropData.y,
          width: this.cropData.width,
          height: this.cropData.height,
        })
          .then(result => {
            console.log('QR scan result', result)
            if (result.match(/^https?:\/\//)) {
              this.guessStatus = 'found'
            } else {
              this.guessStatus = 'not_url'
            }
            this.guessUrl = result
          })
          .catch(() => {
            this.guessStatus = 'not_found'
          })
          .finally(() => this.guessing = false)
      }
    },
    getCropDataFromLink(link) {
      return {
        x: link.x,
        y: link.y,
        width: link.width,
        height: link.height,
      }
    },
    getStroke(link) {
      if (this.selectedLink && link.id === this.selectedLink.id) {
        return '#FF69B4';
      }
      return '#FFA8CF'
    },
    cancelSelect() {
      this.selectedLink = null
      this.linkUrl = null
      this.cropData = null
      this.clearGuess()
    },
    selectLink(link) {
      this.linkUrl = link.url
      this.selectedLink = link
    },
    linkProps(link) {
      return {
        x: (100 * link.xPercentage) + '%',
        y: (100 * link.yPercentage) + '%',
        width: (100 * link.widthPercentage) + '%',
        height: (100 * link.heightPercentage) + '%',
      }
    },
    crop(event) {
      const { naturalWidth, naturalHeight } = event.target
      // This can happen if e.g. the user zooms before they draw a box.
      if (Math.round(event.detail.width) === 0 || Math.round(event.detail.height) === 0) {
        return
      }
      this.cropData = {
        x: Math.round(event.detail.x),
        y: Math.round(event.detail.y),
        width: Math.round(event.detail.width),
        height: Math.round(event.detail.height),
        // We use a percentage to handle the fact that the image will likely be resized (shrunk) when shown.
        xPercentage: event.detail.x / naturalWidth,
        yPercentage: event.detail.y / naturalHeight,
        widthPercentage: event.detail.width / naturalWidth,
        heightPercentage: event.detail.height / naturalHeight,
      }
      this.guess()
    },
    setLink() {
      const linkUrl = this.linkUrl
      if (!linkUrl) {
        this.$snotify.error('Invalid URL')
        return
      }
      const link = {
        url: linkUrl,
        ...this.cropData,
      }
      this.createLink(link)
        .then(response => {
          this.localLinks.push(response.data.link)
          this.cancelAdd()
        })
        .catch(error => {
          this.$snotify.error('There was an error saving the link: ' + error.message)
        })
    },
    editLink() {
      const linkUrl = this.linkUrl
      if (!linkUrl) {
        this.$snotify.error('Invalid URL')
        return
      }
      const link = {
        id: this.selectedLink.id,
        url: linkUrl,
        ...this.cropData,
      }
      this.updateLink(link)
        .then(response => {
          const index = this.localLinks.findIndex(x => x.id === link.id)
          this.localLinks.splice(index, 1, response.data.link)
          this.selectedLink = this.localLinks.find(x => x.id === link.id)
          this.$snotify.success('Your link has been saved', 'Success')
          this.cancelSelect()
        })
        .catch(error => {
          this.$snotify.error('There was an error saving the link: ' + error.message)
        })
    },
    cancelAdd() {
      this.adding = false
      this.cropData = null
      this.linkUrl = null
      this.clearGuess()
    },
    delLink(link) {
      this.$snotify.confirm('Are you sure you want to delete this link?', 'Are you sure?', {
        position: SnotifyPosition.centerCenter,
        backdrop: 0.67,
        buttons: [
          { text: 'Cancel' },
          {
            text: 'Yes',
            bold: true,
            action: toast => {
              return this.deleteLink(link)
                .then(() => {
                  this.localLinks = this.localLinks.filter(x => x.id !== link.id)
                  this.cancelSelect()
                })
                .catch(error => {
                  this.$snotify.error('There was an error deleting the link: ' + error.message)
                })
                .then(() => this.$snotify.remove(toast.id))
            },
          },
        ]
      })
    },
  },
  components: {
    QrCodeStatus,
    VueCropper,
  }
}
</script>

<style scoped="scoped">
  .rect:hover {
    stroke: #FF69B4;
  }
</style>
